import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkPermission, canEditTemps, canDeleteTemps, ACTIONS, RESOURCES } from '../utils/permissions';
import { NETLIFY_FUNCTIONS } from '../config/api';
import { clearSessionExpired } from '../utils/sessionExpiredEvent';
import SessionExpiredModal from '../components/SessionExpiredModal';
import { startAuthentication } from '@simplewebauthn/browser';

// Constantes de rôles
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CONSULTANT: 'consultant',
  VISITEUR: 'visiteur'
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');

      if (token) {
        try {
          // Vérifier la validité du token et récupérer les infos utilisateur
          const response = await fetch(NETLIFY_FUNCTIONS.GET_CURRENT_USER, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            // Token invalide ou expiré
            localStorage.removeItem('auth_token');
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('auth_token');
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Écouter l'événement d'expiration de session déclenché par la couche service
  useEffect(() => {
    const handleSessionExpired = () => {
      if (isAuthenticated) setSessionExpired(true);
    };
    window.addEventListener('session:expired', handleSessionExpired);
    return () => window.removeEventListener('session:expired', handleSessionExpired);
  }, [isAuthenticated]);

  /**
   * Reconnexion en place après expiration de session (sans quitter la page courante).
   * Retourne {success, error?} pour que SessionExpiredModal puisse afficher l'erreur.
   */
  const handleReauth = async (password) => {
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    const result = await login(user?.email, password, rememberMe);
    if (result.success) {
      clearSessionExpired();
      setSessionExpired(false);
      window.dispatchEvent(new CustomEvent('session:renewed'));
    }
    return result;
  };

  /**
   * Déconnexion forcée + redirect /login (bouton "Changer de compte")
   */
  const handleForceLogout = () => {
    clearSessionExpired();
    setSessionExpired(false);
    logout();
    navigate('/login');
  };

  /**
   * Connecte un utilisateur
   * @param {string} email - Email de l'utilisateur
   * @param {string} password - Mot de passe de l'utilisateur
   * @param {boolean} rememberMe - Garder la session active plus longtemps
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(NETLIFY_FUNCTIONS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, rememberMe })
      });

      const data = await response.json();

      if (response.ok) {
        // Stocker le token
        localStorage.setItem('auth_token', data.token);

        // Stocker l'info remember me pour afficher l'expiration
        if (rememberMe) {
          localStorage.setItem('remember_me', 'true');
        }

        // Stocker les infos utilisateur
        setUser(data.user);
        setIsAuthenticated(true);

        return {
          success: true,
          forcePasswordChange: data.forcePasswordChange || false
        };
      } else {
        return {
          success: false,
          error: data.error || 'Erreur de connexion'
        };
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      return {
        success: false,
        error: 'Erreur de connexion. Veuillez réessayer.'
      };
    }
  };

  /**
   * Connecte un utilisateur avec une passkey (WebAuthn)
   * @param {boolean} rememberMe - Garder la session active plus longtemps
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  const loginWithPasskey = async (rememberMe = false) => {
    try {
      // 1. Obtenir les options d'authentification du serveur
      const optionsResponse = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_AUTH_OPTIONS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!optionsResponse.ok) {
        return { success: false, error: 'Impossible de démarrer l\'authentification' };
      }

      const options = await optionsResponse.json();

      // 2. Déclencher l'authentification WebAuthn dans le navigateur
      let authResponse;
      try {
        authResponse = await startAuthentication(options);
      } catch (browserErr) {
        if (browserErr.name === 'NotAllowedError') {
          return { success: false, error: 'Authentification annulée' };
        }
        return { success: false, error: 'Aucune clé de sécurité reconnue sur cet appareil' };
      }

      // 3. Vérifier la réponse côté serveur
      const verifyResponse = await fetch(NETLIFY_FUNCTIONS.WEBAUTHN_AUTH_VERIFY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: authResponse, rememberMe }),
      });

      const data = await verifyResponse.json();

      if (!verifyResponse.ok) {
        return { success: false, error: data.error || 'Authentification échouée' };
      }

      // 4. Stocker le token (même logique que login())
      localStorage.setItem('auth_token', data.token);
      if (rememberMe) {
        localStorage.setItem('remember_me', 'true');
      }

      setUser(data.user);
      setIsAuthenticated(true);

      return {
        success: true,
        forcePasswordChange: data.forcePasswordChange || false,
      };
    } catch (error) {
      console.error('Erreur loginWithPasskey:', error);
      return { success: false, error: 'Erreur de connexion. Veuillez réessayer.' };
    }
  };

  /**
   * Déconnecte l'utilisateur
   */
  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('remember_me');
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Vérifie si l'utilisateur a la permission d'effectuer une action
   * @param {string} action - Action à vérifier (read, write, delete)
   * @param {string} resource - Ressource concernée
   * @returns {boolean}
   */
  const hasPermission = (action, resource) => {
    if (!user || !user.role) {
      return false;
    }

    return checkPermission(user.role, action, resource);
  };

  /**
   * Vérifie si l'utilisateur peut éditer un temps spécifique
   * @param {object} temps - L'enregistrement de temps
   * @returns {boolean}
   */
  const canUserEditTemps = (temps) => {
    if (!user || !user.role) return false;
    return canEditTemps(user.role, temps, user.consultant_id);
  };

  /**
   * Vérifie si l'utilisateur peut supprimer un temps spécifique
   * @param {object} temps - L'enregistrement de temps
   * @returns {boolean}
   */
  const canUserDeleteTemps = (temps) => {
    if (!user || !user.role) return false;
    return canDeleteTemps(user.role, temps, user.consultant_id);
  };

  /**
   * Vérifie si l'utilisateur a un rôle spécifique
   * @param {string} role - Rôle à vérifier
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Vérifie si l'utilisateur est super admin
   * @returns {boolean}
   */
  const isSuperAdmin = () => {
    return user?.role === ROLES.SUPER_ADMIN;
  };

  /**
   * Vérifie si l'utilisateur est consultant
   * @returns {boolean}
   */
  const isConsultant = () => {
    return user?.role === ROLES.CONSULTANT;
  };

  /**
   * Vérifie si l'utilisateur est visiteur
   * @returns {boolean}
   */
  const isVisiteur = () => {
    return user?.role === ROLES.VISITEUR;
  };

  /**
   * Récupère le token d'authentification
   * @returns {string|null}
   */
  const getToken = () => {
    return localStorage.getItem('auth_token');
  };

  /**
   * Met à jour les informations de l'utilisateur connecté
   * @param {object} updatedUser - Nouvelles données utilisateur
   */
  const updateUser = (updatedUser) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUser
    }));
  };

  const [showWelcomeToast, setShowWelcomeToast] = useState(false);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithPasskey,
    logout,
    hasPermission,
    canUserEditTemps,
    canUserDeleteTemps,
    hasRole,
    isSuperAdmin,
    isConsultant,
    isVisiteur,
    getToken,
    updateUser,
    showWelcomeToast,
    setShowWelcomeToast,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal
        isOpen={sessionExpired}
        userEmail={user?.email || ''}
        onReconnect={handleReauth}
        onLogout={handleForceLogout}
      />
    </AuthContext.Provider>
  );
};

/**
 * Hook pour utiliser le contexte d'authentification
 * @returns {AuthContextValue}
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }

  // Ajouter ACTIONS et RESOURCES au contexte retourné
  return {
    ...context,
    ACTIONS,
    RESOURCES
  };
};

// Ré-exporter les constantes pour faciliter l'import
export { ACTIONS, RESOURCES } from '../utils/permissions';

export default AuthContext;
